import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismIcon } from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import type { SupportedCurrencies } from 'frontend-utils/utils/constants/currency-mapping-constants';
import GeneralCurrencyPicker from 'lumen/components/general/currency-picker';
import HotelAndMarketCurrencyProfileCurrencyPicker from 'lumen/components/hotel-and-market/currency/profile-currency-picker';
import HotelAndMarketCurrencyProfilePicker from 'lumen/components/hotel-and-market/currency/profile-picker';
import type {
  Currency,
  CurrencyConversionExchangeRatesProfile,
} from 'lumen/models';

interface HotelAndMarketCurrencyCustomCurrencyPickerSignature {
  Args: {
    fallbackCurrencyCode: SupportedCurrencies;
    isReadOnly?: boolean;
    onCurrencyChange: (currency: SupportedCurrencies | null) => void;
    onProfileChange: (profile: string | null) => void;
    selectedCurrencyCode: SupportedCurrencies;
    selectedProfileId?: string | null;
  };
}

export default class HotelAndMarketCurrencyCustomCurrencyPicker extends Component<HotelAndMarketCurrencyCustomCurrencyPickerSignature> {
  @service declare currency: Services['currency'];
  @service declare currencyConversion: Services['currency-conversion'];
  @service declare errorTracking: Services['error-tracking'];

  fetchProfilesTask = task({ restartable: true }, async () => {
    try {
      await this.currencyConversion.getBaseCurrenciesTask.perform();
      return await this.currencyConversion.getExchangeRatesProfilesTask.perform();
    } catch (error) {
      this.errorTracking.captureException(error);
    }
  });

  get currencies(): Currency[] {
    return this.currencyConversion.getBaseCurrenciesTask.last?.value ?? [];
  }

  get favouriteCurrencies(): Currency[] {
    return this.currencies.filter((currency) => currency.favourite);
  }

  get profiles(): CurrencyConversionExchangeRatesProfile[] {
    return this.fetchProfilesTask.last?.value ?? [];
  }

  get selectedCurrencyCode(): SupportedCurrencies {
    return this.currency.selectedCurrency ?? this.args.selectedCurrencyCode;
  }

  get selectedProfile(): CurrencyConversionExchangeRatesProfile | null {
    const selectedProfileId =
      this.args.selectedProfileId ?? this.currencyConversion.selectedProfileId;
    return selectedProfileId
      ? (this.profiles.find((p) => p.id === selectedProfileId) ?? null)
      : null;
  }

  constructor(
    owner: Owner,
    args: HotelAndMarketCurrencyCustomCurrencyPickerSignature['Args'],
  ) {
    super(owner, args);

    void this.fetchProfilesTask.perform();
  }

  @action
  handleCurrencyChange(currency: SupportedCurrencies | null): void {
    this.args.onCurrencyChange(currency);
    this.currency.setCurrency(currency);
  }

  @action
  handleProfileChange(
    profile: CurrencyConversionExchangeRatesProfile | null,
  ): void {
    const { selectedCurrencyCode } = this;

    this.currencyConversion.setSelectedProfileId(profile?.id ?? null);

    if (!profile) {
      // If "latest" profile selected, no profile ID
      this.handleCurrencyChange(selectedCurrencyCode);
      this.args.onProfileChange(null);
      return;
    }

    // When profile is set, default to base currency if:
    // 1. No currency is selected, or
    // 2. The selected currency is not available in the selected profile
    const baseCurrencyCode = profile.baseCurrency?.code ?? null;

    // Check if current selected currency is available in the selected profile
    const isCurrentCurrencyAvailable =
      (baseCurrencyCode && selectedCurrencyCode === baseCurrencyCode) ||
      profile.rates.some((rate) => rate.currencyCode === selectedCurrencyCode);

    // If current currency not available, switch to base currency
    if (!isCurrentCurrencyAvailable && baseCurrencyCode) {
      this.handleCurrencyChange(baseCurrencyCode);
    }
    this.args.onProfileChange(profile.id);
  }

  <template>
    <div class="flex items-center">
      <HotelAndMarketCurrencyProfilePicker
        @isReadOnly={{@isReadOnly}}
        @onProfileChange={{this.handleProfileChange}}
        @profiles={{this.profiles}}
        @selectedProfile={{this.selectedProfile}}
      />

      <PrismIcon @name="arrow-right" />

      {{#if this.selectedProfile}}
        <HotelAndMarketCurrencyProfileCurrencyPicker
          @favouriteCurrencies={{this.favouriteCurrencies}}
          @onCurrencyChange={{this.handleCurrencyChange}}
          @selectedCurrencyCode={{this.selectedCurrencyCode}}
          @selectedProfile={{this.selectedProfile}}
        />
      {{else}}
        <GeneralCurrencyPicker
          @defaultCurrencyTooltip={{t "revenue_insight.currency.local_in_use"}}
          @fallbackCurrencyCode={{@fallbackCurrencyCode}}
          @onCurrencyChange={{this.handleCurrencyChange}}
        />
      {{/if}}
    </div>
  </template>
}
